import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Users, CreditCard, MessageSquareText, Phone, Crown, Shield, DollarSign, TrendingUp, Video } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPage = ({ user }) => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('kaplie_token');
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/users`, { headers }),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
      trialing: 'bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/20',
      past_due: 'bg-red-500/10 text-red-400 border-red-500/20',
      canceled: 'bg-white/[0.03] text-zinc-500 border-white/[0.06]',
      inactive: 'bg-white/[0.03] text-zinc-600 border-white/[0.06]',
    };
    return styles[status] || styles.inactive;
  };

  const getPlanBadge = (plan) => {
    if (plan === 'pro') return 'bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/20';
    if (plan === 'basic') return 'bg-white/[0.06] text-zinc-300 border-white/[0.08]';
    return 'bg-white/[0.03] text-zinc-600 border-white/[0.06]';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white/10 border-t-[#D4AF37] rounded-full animate-spin" />
      </div>
    );
  }

  const statCards = stats ? [
    { icon: Users, label: 'Total users', value: stats.total_users, color: 'text-zinc-300', bg: 'bg-white/[0.06]' },
    { icon: CreditCard, label: 'Active subs', value: stats.active_subscriptions, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
    { icon: DollarSign, label: 'Monthly revenue', value: `$${stats.monthly_revenue}`, color: 'text-[#D4AF37]', bg: 'bg-[#D4AF37]/10' },
    { icon: TrendingUp, label: 'Plans split', value: `${stats.basic_plans} / ${stats.pro_plans}`, sub: 'Basic / Pro', color: 'text-zinc-300', bg: 'bg-white/[0.06]' },
  ] : [];

  return (
    <div className="min-h-screen bg-[#050505] text-white font-inter">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-[#D4AF37]/[0.04] rounded-full blur-[160px]" />
      </div>

      <nav className="relative z-10 border-b border-white/[0.06] backdrop-blur-2xl bg-black/60 sticky top-0">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-4 flex items-center gap-3">
          <Link to="/dashboard">
            <button className="p-2 hover:bg-white/[0.06] rounded-lg transition-colors" data-testid="back-to-dashboard-btn">
              <ArrowLeft className="w-5 h-5 text-zinc-400" />
            </button>
          </Link>
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#D4AF37] to-[#C5A059] flex items-center justify-center">
              <Crown className="w-3.5 h-3.5 text-black" />
            </div>
            <h1 className="font-satoshi font-bold text-lg tracking-tight">Admin</h1>
          </div>
        </div>
      </nav>

      <main className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 py-10">
        <div className="mb-8">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#D4AF37]/80 mb-2">Control Room</p>
          <h2 className="font-satoshi font-bold text-3xl tracking-tight">Dashboard overview</h2>
        </div>

        {/* Stat Grid */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {statCards.map((s, i) => (
              <div key={i} className="p-5 rounded-2xl bg-white/[0.03] border border-white/[0.06] hover:border-white/[0.12] transition-all" data-testid={`stat-card-${i}`}>
                <div className="flex items-center gap-2 mb-3">
                  <div className={`w-7 h-7 rounded-md ${s.bg} flex items-center justify-center`}>
                    <s.icon className={`w-3.5 h-3.5 ${s.color}`} />
                  </div>
                  <span className="text-xs uppercase tracking-wider font-medium text-zinc-500">{s.label}</span>
                </div>
                <p className="font-satoshi font-bold text-3xl tracking-tighter">{s.value}</p>
                {s.sub && <p className="text-xs text-zinc-600 mt-1">{s.sub}</p>}
              </div>
            ))}
          </div>
        )}

        {/* Secondary Stats */}
        {stats && (
          <div className="grid grid-cols-2 gap-4 mb-8">
            <div className="p-5 rounded-2xl bg-white/[0.03] border border-white/[0.06] flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center">
                <MessageSquareText className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-zinc-500 mb-0.5">Saved conversations</p>
                <p className="font-satoshi font-bold text-2xl tracking-tighter">{stats.total_conversations}</p>
              </div>
            </div>
            <div className="p-5 rounded-2xl bg-white/[0.03] border border-white/[0.06] flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-white/[0.06] flex items-center justify-center">
                <Phone className="w-5 h-5 text-zinc-300" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-zinc-500 mb-0.5">Total calls</p>
                <p className="font-satoshi font-bold text-2xl tracking-tighter">{stats.total_calls}</p>
              </div>
            </div>
          </div>
        )}

        {/* Users Table */}
        <div className="rounded-2xl backdrop-blur-2xl bg-black/60 border border-white/[0.08] overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.4)]">
          <div className="px-6 py-5 border-b border-white/[0.06] flex items-center justify-between">
            <h3 className="font-satoshi font-bold flex items-center gap-2">
              <Users className="w-4 h-4 text-zinc-400" />
              All users
            </h3>
            <span className="text-xs uppercase tracking-wider text-zinc-500">{users.length} total</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-zinc-500 bg-white/[0.02]">
                  <th className="px-6 py-3 font-medium text-xs uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 font-medium text-xs uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 font-medium text-xs uppercase tracking-wider">Plan</th>
                  <th className="px-6 py-3 font-medium text-xs uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 font-medium text-xs uppercase tracking-wider">Joined</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t border-white/[0.04] hover:bg-white/[0.02] transition-colors" data-testid={`user-row-${u.id}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#D4AF37]/15 to-[#C5A059]/5 border border-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-[#D4AF37]">{(u.name || '?').charAt(0).toUpperCase()}</span>
                        </div>
                        <span className="font-medium text-white">{u.name || '-'}</span>
                        {u.is_admin && <Crown className="w-3.5 h-3.5 text-[#D4AF37]" />}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-zinc-400">{u.email}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-semibold tracking-wider uppercase border ${getPlanBadge(u.plan_type)}`}>
                        {u.plan_type === 'pro' ? 'Pro' : u.plan_type === 'basic' ? 'Basic' : 'None'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-semibold tracking-wider uppercase border ${getStatusBadge(u.subscription_status)}`}>
                        {u.subscription_status || 'inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-zinc-500 text-xs">{formatDate(u.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminPage;
